import { Icon, ImageIcon } from "@phosphor-icons/react";
import { shouldUseCoverFit } from "@ac-expo/utils";
import { useEffect, useRef, useState } from "react";
import { PropsWithClassName } from "../../types/PropsWithClassName";
import { cn } from "../../utils";

export type ThumbnailObjectFit = "auto" | "contain" | "cover";

export type ThumbnailProps = PropsWithClassName<{
  imageUrl: string | null;
  altContent?: string;
  NoImageIcon?: Icon;
  noImageIconSize?: number;
  objectFit?: ThumbnailObjectFit;
}>;

export function Thumbnail({
  imageUrl,
  altContent,
  className,
  NoImageIcon = ImageIcon,
  noImageIconSize = 56,
  objectFit = "contain",
}: ThumbnailProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [autoFit, setAutoFit] = useState<"contain" | "cover">("contain");
  const imgRef = useRef<HTMLImageElement>(null);

  const syncFromImg = () => {
    const img = imgRef.current;
    if (!img?.naturalWidth || !img.naturalHeight) return;
    setAutoFit(
      shouldUseCoverFit(img.naturalWidth, img.naturalHeight)
        ? "cover"
        : "contain",
    );
  };

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setAutoFit("contain");
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
      syncFromImg();
    }
  }, [imageUrl]);

  useEffect(() => {
    if (objectFit !== "auto") return;
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      syncFromImg();
    }
  }, [objectFit]);

  const handleLoad = () => {
    setIsLoaded(true);
    syncFromImg();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const effectiveFit = objectFit === "auto" ? autoFit : objectFit;

  return (
    <div
      className={cn(
        "relative flex aspect-[1280/486] w-full items-center justify-center overflow-hidden rounded-xl bg-white",
        {
          "bg-secondary-50": !imageUrl,
        },
        className,
      )}
    >
      {imageUrl && !hasError ? (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 z-20 animate-pulse bg-secondary-100" />
          )}
          <img
            ref={imgRef}
            src={imageUrl}
            alt={`Illustration de ${altContent}`}
            className={cn(
              "z-10 transition-opacity duration-300",
              effectiveFit === "contain"
                ? "h-full max-w-full object-contain"
                : "size-full object-cover",
              isLoaded ? "opacity-100" : "opacity-0",
            )}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
          />
          {effectiveFit === "contain" && (
            <img
              src={imageUrl}
              alt={`Arrière-plan flouté de ${altContent}`}
              className={cn(
                "absolute left-0 top-0 size-full scale-110 blur transition-opacity duration-300",
                isLoaded ? "opacity-100" : "opacity-0",
              )}
              loading="lazy"
            />
          )}
        </>
      ) : (
        <NoImageIcon size={noImageIconSize} className="text-secondary-500" />
      )}
    </div>
  );
}

export default Thumbnail;
